import Http from './axios-config';

/** The superAdmin settings that take effect immediately (GET/PUT /admins/settings). */
class AppSettingsService {

    constructor() {
        this.http = new Http();
    }

    async getSettings() {
        return await this.http.get('/admins/settings');
    }

    /** The same settings, for a page that only mentions them: a failure shows no error toast. */
    async getSettingsQuietly() {
        return await this.http.get('/admins/settings', { silentErrors: true });
    }

    async setGpLetterAutoSendMode(mode) {
        return await this.http.put('/admins/settings/gp-letter-auto-send-mode', { mode });
    }

    async setPatientListRestriction(enabled) {
        return await this.http.put('/admins/settings/patient-list-restriction', { enabled });
    }

    async setFullPatientAccess(clinicianId, enabled) {
        return await this.http.put(`/admins/settings/clinicians/${clinicianId}/full-patient-access`, { enabled });
    }

    /**
     * Change a clinician's role (1 Doctor, 4 Admin, 3 Super admin). Ends every session they have.
     * `fromRole` is the role this page shows: if someone changed it since, the server refuses (409)
     * rather than let this page undo their change.
     */
    async changeClinicianRole(clinicianId, role, fromRole) {
        return await this.http.put(`/admins/settings/clinicians/${clinicianId}/role`, { role, fromRole });
    }

    /** Sign a clinician out of every session. Never touches their role. */
    async signOutClinician(clinicianId) {
        return await this.http.post(`/admins/settings/clinicians/${clinicianId}/sign-out`);
    }

    async getClinicianRoleHistory(clinicianId) {
        return await this.http.get(`/admins/settings/clinicians/${clinicianId}/role-changes`);
    }

    async getClinicianConsultationStatus(clinicianId) {
        return await this.http.get(`/admins/settings/clinicians/${clinicianId}/consultation-status`);
    }

    /**
     * Which gated sections the signed-in admin may see, and their effective role. Asked on every
     * navigation, so it never toasts: the caller falls back quietly (see SectionAccessContext).
     * A 401 still goes through the usual refresh / sign-out.
     */
    async getVisibleSections() {
        return await this.http.get('/admins/settings/visible-sections', { silentErrors: true });
    }

    /** Every section's audience, with who changed it last. superAdmin only. */
    async getSectionAccess() {
        return await this.http.get('/admins/settings/section-access');
    }

    /**
     * Open a section to Doctors and Admins ('all'), to Admins and super admins ('admins'), or limit it
     * to super admins ('superAdmin').
     * `expectedAudience` is what this page shows: if someone changed it since, the server refuses
     * (409) rather than let this page undo their change.
     */
    async setSectionAccess(section, audience, expectedAudience) {
        return await this.http.put('/admins/settings/section-access', { section, audience, expectedAudience });
    }

    /**
     * Every staff member with when they were last active: { serverTime, staff: [{ id, name, role,
     * lastSeenAt, online }] }. superAdmin only (403 for anyone else). The Staff activity page asks
     * every 30 seconds and shows its own notice when this fails, so it never toasts.
     * A 401 still goes through the usual refresh / sign-out.
     */
    async getStaffActivity() {
        return await this.http.get('/admins/settings/staff-activity', { silentErrors: true });
    }
}

export default new AppSettingsService();
